import { useState } from "react";
import { Link } from "@tanstack/react-router";

import {
  useDismissPendingAction,
  useResolvePendingAction,
} from "@/lib/api/pending-actions";
import { useRefillSessions } from "@/lib/api/refill-sessions";
import type { Grow, PendingAction } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { UrgencyBadge } from "@/components/urgency-badge";
import { EditActionForm } from "./action-form";

// ActionRow renders one open pending action with its PRIMARY actions visible
// inline (Open grow / Start refill / Mark done / Dismiss / Skip) rather than
// hidden behind a click-to-expand. A chevron toggles a details panel that
// shows the full rationale + provenance (the source refill, when linked) and
// an inline Edit form.
//
// "Mark done" and "Skip" record a REAL resolution via the `:resolve` endpoint
// ("completed" vs "skipped" with an optional reason); the server writes an
// audit row against the owning grow so the outcome shows up in its activity
// log. "Dismiss" still uses the soft DELETE-dismiss (which now also logs a
// resolution="dismissed" to the grow). Both hooks invalidate the per-grow
// cache, so a resolved row drops off the open list.
export function ActionRow({
  row,
  grow,
}: {
  row: PendingAction;
  grow?: Grow;
}) {
  const dismiss = useDismissPendingAction(row.id, row.grow_id);
  const resolve = useResolvePendingAction(row.id, row.grow_id);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const busy = dismiss.isPending || resolve.isPending;

  function onSkip() {
    const reason = window.prompt("Reason for skipping? (optional)") || undefined;
    resolve.mutate({ resolution: "skipped", reason });
  }

  return (
    <li className="px-4 py-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Hide details" : "Show details"}
        className="flex w-full min-w-0 items-start gap-2 text-left"
      >
        <span className="mt-1 text-xs text-muted-foreground">
          {open ? "▾" : "▸"}
        </span>
        <span className="min-w-0 flex-1 space-y-1">
          {grow && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
              🌱 {grow.species} · {grow.system_type}
            </span>
          )}
          <span className="flex flex-wrap items-center gap-2">
            <UrgencyBadge urgency={row.urgency} />
            <span className="font-medium">{row.title}</span>
          </span>
          {row.rationale && !open && (
            <span className="block text-sm text-muted-foreground line-clamp-1">
              {row.rationale}
            </span>
          )}
        </span>
      </button>

      {/* Primary actions, always visible inline on open rows. */}
      <div className="mt-3 flex flex-wrap items-center gap-2 pl-6">
        <Button size="sm" variant="outline" asChild>
          <Link to="/grows/$growId" params={{ growId: row.grow_id }}>
            Open grow
          </Link>
        </Button>
        <PrimaryCTA row={row} />
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => resolve.mutate({ resolution: "completed" })}
        >
          Mark done
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={onSkip}
        >
          Skip
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => {
            const reason = window.prompt("Reason?") || undefined;
            dismiss.mutate(reason);
          }}
        >
          Dismiss
        </Button>
      </div>

      {open && (
        <div className="mt-3 space-y-3 pl-6">
          {row.rationale && (
            <p className="text-sm text-muted-foreground">{row.rationale}</p>
          )}
          {/* "why" / provenance: where this action came from. The llm_call
              itself is admin-only (no link), so an AI-suggested action gets a
              muted chip; a multi-step plan exposes its step number; and a
              refill-derived action links back to its source refill. */}
          {(row.llm_call_id || row.sequence != null) && (
            <div className="flex flex-wrap items-center gap-1.5">
              {row.llm_call_id && (
                <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2 py-0.5 text-xs font-medium text-violet-600 dark:text-violet-300">
                  ✨ AI-suggested
                </span>
              )}
              {row.sequence != null && (
                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  Step {row.sequence}
                </span>
              )}
            </div>
          )}
          {row.derived_from_refill_id && (
            <Link
              to="/refills/$sessionId"
              params={{ sessionId: row.derived_from_refill_id }}
              className="inline-block text-sm underline"
            >
              From this refill →
            </Link>
          )}
          {editing ? (
            <EditActionForm row={row} onDone={() => setEditing(false)} />
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

// PrimaryCTA renders the single PRIMARY action for a pending action, derived
// from its typed `cta_kind`. Each kind maps to a SPECIFIC route + label; an
// empty/unknown kind (or a characterize CTA missing its target) falls back to
// the generic "Start refill →" link so there's no regression vs. the prior
// behaviour. TanStack Router's <Link> is route-typed (each destination needs
// its own params/search shape), so we branch on the kind rather than feed a
// single dynamic <Link> a computed descriptor.
function PrimaryCTA({ row }: { row: PendingAction }) {
  switch (row.cta_kind) {
    case "create_water_source":
      return (
        <Button size="sm" asChild>
          <Link to="/chemistry-profile/water-sources/new">
            Create water source →
          </Link>
        </Button>
      );
    case "characterize_water_source":
      // Needs a target water-source id to know WHICH source to characterize.
      // If it's missing we fall back to the create-water-source link (the safe
      // option: a brand-new source is still a sensible next step, and the edit
      // route would 404 / break without a $waterSourceId param).
      if (row.cta_target_id) {
        return (
          <Button size="sm" asChild>
            <Link
              to="/chemistry-profile/water-sources/$waterSourceId/edit"
              params={{ waterSourceId: row.cta_target_id }}
            >
              Characterize water →
            </Link>
          </Button>
        );
      }
      return (
        <Button size="sm" asChild>
          <Link to="/chemistry-profile/water-sources/new">
            Create water source →
          </Link>
        </Button>
      );
    case "assign_water_source":
      // Assigning the grow's default water source happens on the grow's edit
      // page (the form's "Water source" picker).
      return (
        <Button size="sm" asChild>
          <Link to="/grows/$growId/edit" params={{ growId: row.grow_id }}>
            Assign water source →
          </Link>
        </Button>
      );
    case "start_refill":
    default:
      // Explicit start_refill, plus the empty/unknown fallback. Context-aware:
      // if a refill is already open for this grow, resume it instead of
      // starting a second one (which would split the work across two sessions).
      return <StartOrResumeRefillCTA growID={row.grow_id} />;
  }
}

// StartOrResumeRefillCTA shows "Resume refill →" (into the in-progress session's
// wizard) when an open session exists for the grow, else "Start refill →". This
// fixes being offered "Start refill" while already mid-refill.
function StartOrResumeRefillCTA({ growID }: { growID: string }) {
  const sessions = useRefillSessions({ grow_id: growID });
  const open = (sessions.data?.items ?? []).find(
    (s) => !s.closed_at && !s.voided_at,
  );
  if (open) {
    return (
      <Button size="sm" asChild>
        <Link
          to="/refills/$sessionId/wizard"
          params={{ sessionId: open.id }}
          search={{ step: 1 }}
        >
          Resume refill →
        </Link>
      </Button>
    );
  }
  return (
    <Button size="sm" asChild>
      <Link to="/refills/new" search={{ grow: growID }}>
        Start refill →
      </Link>
    </Button>
  );
}
